/**
 * billing.ts — деньги кабинета (WEB-1874, этап 3, порция S3).
 *
 * Один словарь на весь денежный контур: тариф и кошелёк («Баланс/Тариф»),
 * пополнение кредитов, история операций, подключение Stripe и его каталог,
 * магазин (товары, подписки, покупатели). Разделять их по файлам не за что —
 * экраны переходят один в другой, а половина надписей общая (провайдер,
 * статус подписки, «возврат»), и вторая копия слова разъехалась бы первой же
 * правкой.
 *
 * ⚠ ГРАНИЦА КАРТОЧКИ (R4, деньги). Здесь переводятся ТОЛЬКО ПОДПИСИ. Ни одна
 * формула, ни один порог, ни одна единица (центы ↔ валюта, доли ↔ проценты)
 * этой волной не менялись: суммы по-прежнему считает `utils/formatCents.ts`
 * и `utils/zeroDecimalCurrencies.ts`, а печатает `i18n/format.ts` — русская
 * половина даёт те же строки, что прежние `toLocaleString('ru-RU')`,
 * английская отличается только разделителями, которые рисует `Intl`.
 *
 * ⚠ ЧТО НЕ ПЕРЕВОДИТСЯ. Имена собственные (Stripe, Free/Pro, Cr),
 * коды валют и коды операций (`DEPOSIT`, `checkout.session.completed`) —
 * это идентификаторы, а не надписи. Описания транзакций приходят с сервера
 * по-английски и переводятся на отображении
 * (`utils/localizeTransactionDescription.ts`), историю в базе не переписываем.
 */
import { defineDictionary } from '../define';
import { plural } from '../plural';

/** WEB-4180: английский порядковый хвост числа (1st, 2nd, 3rd, 1,000th). */
function enOrdinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  return ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
}

export const billingStrings = defineDictionary(
  {
    // ── Вкладка «Баланс/Тариф»: загрузка ────────────────────────────────────
    balanceLoadFailed: 'Could not load the balance',
    balanceLoadFailedHint: 'Check your connection and try again',
    // WEB-2806: лимит трат организации на ИИ в сутки и в месяц.
    aiSpendLimitTitle: 'AI spending limit',
    // WEB-3761 юнит «лимит трат»: тексты по эталону (org.jsx, TB.en limit…limNo).
    aiSpendLimitHelp:
      'An AI launch over the limit will not start, no credits are charged',
    // WEB-3808: лимит вводится целыми долларами, траты — суммой `$12.34`.
    aiSpendLimitDaily: 'Per day, $',
    aiSpendLimitMonthly: 'Per month, $',
    aiSpendLimitNone: 'No limit',
    aiSpendLimitToday: 'Today',
    aiSpendLimitMonth: 'This month',
    aiSpendLimitHeld: 'Held',
    aiSpendLimitHeldTip: 'Held by running AI actions — charged when they finish',
    aiSpendLimitTimezone: (tz: string) =>
      `The day and the month follow the organization owner’s time zone: ${tz}`,
    aiSpendLimitSave: 'Save',
    aiSpendLimitSaved: 'Limit saved',
    aiSpendLimitInvalid: 'A whole number from 1',
    aiSpendLimitNoAccess: 'Only whoever manages the money can change the limit',
    aiSpendLimitSaveFailed: 'Could not save the limit',
    aiSpendLimitLoadFailed: 'Could not load the AI spending limit',

    // ── Текущий тариф ───────────────────────────────────────────────────────
    planOwnerOnly: 'The organization owner manages the plan and payments',
    planHiddenReason:
      'The billing part is hidden by access settings: the plan, amounts and transaction history are visible to whoever manages the money of the organization.',
    perMonthShort: '/mo',
    planTrialUntil: (until: string) => `Trial until ${until}`,
    // WEB-4125 (эталон org.jsx:608, 663): цена — planTerms ответа, уже со знаком валюты
    planTrialUntilWithPrice: (until: string, price: string) =>
      `Trial until ${until}, then ${price}/mo`,
    planCancelsOn: (date: string) => `The subscription will be canceled on ${date}`,
    planRenewsOn: (date: string) => `Renews on ${date}`,
    planGrantedManually: 'The plan was granted manually — no renewal date',
    noProjectsSettingUnavailable:
      'The organization has no projects yet — this setting is unavailable.',
    settingChangeFailed: 'Could not change the setting',
    noProjectsBillingUnavailable:
      'The organization has no projects yet — billing is unavailable.',
    portalOpenFailed: 'Could not open the billing portal',
    pastDueTitle: 'The payment failed — update your card',
    pastDueNoDeadline:
      'We will try to charge the card again over the next 3 days. If the payment does not go through, the plan will be downgraded to Free.',
    updateCard: 'Update card',
    paidFromBalanceTitle: 'The card was declined — the plan was paid from your balance',
    paidFromBalanceText:
      'We covered this billing period from your credit balance. Update the card so the next charge goes through as usual.',
    manageSubscription: 'Manage the subscription',
    payFromBalanceToggle: 'Pay the plan from the balance if the card fails',

    // WEB-3302 юнит «тариф» — карточка тарифа, плашки и «Начните пробный
    // период» на ките (эталон design/cabinet-rescin/sections/org.jsx, TB.en:
    // plan, planHelp, st, unpaidT, unpaidD, pastD, noneT, trialRule, draftOnly,
    // noneBtn, fromBalD). «Оплатить тариф» — без суммы до юнита «после сервера»
    // (тот же текст, что abList.tierPay).
    planLabel: 'Plan',
    planHelp: 'One plan: everything included, no limits. Plus a fee per exit',
    planStatusActive: 'Active',
    planStatusTrial: 'Trial',
    planStatusNotPaid: 'Not paid',
    planStatusPaymentFailed: 'Payment failed',
    planUnpaidTitle: 'The plan is not paid',
    planUnpaidText: 'Publishing, domains and AI are closed',
    // WEB-4008: сумма — из planTerms ответа сервера (null — суммы нет).
    planPay: (amount: string | null) => (amount ? `Pay ${amount}` : 'Pay for a plan'),
    planPastDueUntil: (deadline: string) =>
      `Pages keep working until ${deadline}, then “not paid”`,
    planNoneTitle: 'Start your trial',
    // WEB-4008: дни и переходы — planTerms.trial ответа сервера.
    planNoneRule: (days: number, exits: number, exitsText: string) =>
      `${days} ${plural('en', days, { one: 'day', other: 'days' })} or ${exitsText} ${plural('en', exits, { one: 'exit', other: 'exits' })} — whichever comes first`,
    planNoneStart: 'Start trial',
    // WEB-4008 — тексты с числами из planTerms / trialProgress (эталон TB.en:
    // noneHelp, noneVolume, choose, perMonth, perYear, perYearSave, days,
    // trans, trLeft, of, trialWarn); подписи до нажатия — ответ сессии 52.
    // WEB-4180: noneHelp эталона c9ab75a2 с суммами из planTerms и правилом
    // пробного; суммы гранта нет (условие сессии 52, п. 3).
    planNoneHelp: (rule: string, monthly: string, annualMonthly: string) =>
      `${rule}. A card is required. Then ${monthly} a month, or ${annualMonthly} a month billed yearly. Cancel any time. The trial is given once per person. Before the trial — one draft project, no publishing. You cannot buy a domain on the trial — you can connect your own. On your first payment — AI credits as a gift`,
    planPeriodLabel: 'Billing period',
    planPeriodMonth: (amount: string) => `Monthly ${amount}`,
    planPeriodYear: (amountPerMonth: string) => `Yearly ${amountPerMonth}/mo`,
    // WEB-4180: earlyLine эталона c9ab75a2 — граница переходов из planTerms.
    planTrialEarlyEnd: (exits: number, exitsText: string) =>
      `At the ${exitsText}${enOrdinal(exits)} exit the trial ends early — and so does the charge`,
    planOwnerFixes: 'The owner can fix this',
    /** Отказ 403 оформления тарифа с карточки — вместо текста сервера (WEB-4008). */
    planCheckoutForbidden: 'Only the owner or an admin with billing access can set up the plan',
    // ── WEB-4180: карточки периода (TrialPick), окно TrialAsk и плашка оплаты
    // по эталону рескина c9ab75a2 (TB.en: pkM, pkY, payNowM/Y, payM/Y, usedT,
    // usedHelp, askWhy, endedT/D, saveLine); суммы — из planTerms ответа.
    planPickMonthLabel: 'Monthly',
    planPickMonthNote: 'Charged every month',
    planPickYearLabel: 'Yearly',
    planPickPerMonth: (amount: string) => `${amount}/mo`,
    planPickYearNote: (charge: string, save: string | null) =>
      save ? `${charge} at once, save ${save}` : `${charge} at once`,
    planPayNow: (amount: string) => `Pay ${amount}`,
    planChargeNow: (amount: string) => `we'll charge ${amount}`,
    planPayMonth: (amount: string) => `Pay ${amount} for a month`,
    planPayYear: (amount: string) => `Pay ${amount} for a year`,
    planGetTitle: 'Get the plan',
    // Тексты веток «уже был у вас» / «уже взят организацией» — предложение
    // сессии 52, утверждены владельцем 03.11; сумма — под кнопкой карточки.
    planUsedByYou: "You've already used your trial — the plan starts right away",
    planUsedByOrg: 'This organization has already used its trial — the plan starts right away',
    planUsedHelpYou:
      'The trial is given once per person — you already used it in another organization. A card is required, cancel any time. On your first payment — AI credits as a gift',
    planUsedHelpOrg:
      'A card is required, cancel any time. On your first payment — AI credits as a gift',
    planSaveLine: (annualMonthly: string, save: string) =>
      `${annualMonthly}/mo billed yearly — save ${save}`,
    planEndedTitle: 'Your trial has ended',
    planEndedText: 'Pay for the plan — everything works again',
    trialAskWhyPublish: 'Publishing opens with the trial',
    trialAskWhyProject: 'A second project comes with the trial',
    trialAskWhyLimit: 'This opens with the trial',
    trialAskWhyAi: 'Pro mode and link parsing come with the trial',
    trialAskToPlan: 'Go to plan',
    // ── WEB-3302 юнит «условия»: окно «Тарифы и условия» (эталон TB.en.pr,
    // org.jsx:698–708). Числа — аргументы от planTerms ответа, не литералы;
    // в подсказке «больший из двух» суммы эталона «$1» нет — её нет в плане.
    prTitle: 'Plans and terms',
    prHelp:
      'One subscription per organization. Volume fee per project: own payments — per exit, payments through us — a percentage of revenue',
    prColOwn: 'Own payments',
    prColOwnSub: 'you take payments yourself',
    prColUs: 'Payments through us',
    prColUsSub: 'the buyer pays via our Stripe',
    prColEnt: 'Enterprise',
    prColEntSub: 'large volume, custom terms',
    prDeal: 'by contract',
    prGrpFunnels: 'Funnels and builder',
    prGrpAnalytics: 'Analytics and ads',
    prGrpApp: 'App, domains, team',
    prGrpPay: 'Payments and volume',
    prGrpStart: 'Start and charges',
    prRowFunnels: 'Quizzes, paywalls and funnels',
    prRowAi: 'AI: funnel generation and edits',
    prRowAnalytics: 'Analytics and A/B tests',
    prRowEvents: 'Server-side events to Meta and TikTok',
    prRowApp: 'App connection: SDK, keys, events to your server',
    prRowDomains: 'Own domains with a certificate',
    prRowMembers: 'Members and roles',
    prRowBuyer: 'Buyer payment',
    prRowVolume: 'Volume fee',
    prRowTiers: 'Tiers',
    prRowStart: 'How to start',
    prRowCharges: 'Charges',
    prUnlimited: 'unlimited',
    prForCredits: 'for credits',
    prNoExtra: 'no extra charge',
    prOnYourSide: 'on your side',
    prViaStripe: 'via our Stripe',
    prPerRedirect: 'per paywall redirect',
    prBoth: 'the larger of the two: % of revenue or exits',
    prBothTip:
      'Both are counted for each project and the larger one is charged — so a small payment does not zero out the exit fee. The percentage turns on once unified revenue tracking works; until then — exits',
    prStartEnt: 'After a call with us',
    prInvoiceEnt: 'Invoice by contract',
    prIncTrial: 'On the trial — everything except AI credits and buying a domain',
    prTrial: (days: number, exits: number, exitsText: string) =>
      `Trial: ${days} ${plural('en', days, { one: 'day', other: 'days' })} or ${exitsText} ${plural('en', exits, { one: 'exit', other: 'exits' })}, card required`,
    prThreshold: (amount: string) =>
      `${amount} accrued — charged at once, the rest at month end`,
    prYearSave: (annual: string, savings: string) =>
      `yearly — ${annual}, ${savings} cheaper`,
    prYearOnly: (annual: string) => `yearly — ${annual}`,
    tierFirst: (count: string) => `First ${count}`,
    tierFree: 'free',
    tierUpTo: (count: string) => `Up to ${count}`,
    tierRange: (from: string, to: string) => `${from}–${to}`,
    tierAbove: (count: string) => `Above ${count}`,
    tierAny: 'Every exit',
    pctUpTo: (amount: string) => `Up to ${amount}`,
    pctAbove: (amount: string) => `Above ${amount}`,
    pctAny: 'Any revenue',
    prOff: 'Plan terms are unavailable right now — refresh the page later',
    // WEB-3302 юнит «условия»: плашка долга по объёму (эталон TB.en debtT,
    // debtD, debtBtn, org.jsx:672) и исходы «Оплатить долг» (сессия 52, 30.10).
    debtT: (amount: string | null) =>
      amount ? `Exit fees owed — ${amount}` : 'Exit fees owed',
    debtD: 'The plan charge is paused until the debt is paid',
    debtBtn: 'Pay the debt',
    usageDebtNotPayable:
      'The debt could not be settled: the invoice cannot be paid. Please contact support.',
    usageDebtNothingDue: 'There is no debt to pay.',
    usageDebtPayFailed: 'Could not pay the debt — try again',
    trialDaysLabel: 'Days',
    trialExitsLabel: 'Exits',
    trialExitsLeft: (count: number, formatted: string) =>
      `${formatted} ${plural('en', count, { one: 'exit', other: 'exits' })} left`,
    trialOf: (used: string, total: string) => `${used} of ${total}`,
    trialThenPaid: 'then the paid month starts',
    payFromBalanceHint: 'Only purchased credits and only in full — gift credits do not pay for the plan',

    // ── Пробный период ──────────────────────────────────────────────────────
    trialDaysLeftTag: (days: number) => `${days} d left`,

    // ── Кошелёк ─────────────────────────────────────────────────────────────
    // WEB-1874 (часть 2): подписи лотов кошелька по машинному коду сервера
    // (WALLET_LOT_LABEL_CODES в backend/src/billing/utils/wallet-lot-mapper.ts;
    // совпадение множеств держит WalletLotLabel.web1874.test.tsx).
    lotTrialGrant: 'Trial grant',
    lotOnboardingGrant: 'Welcome grant',
    lotAdminRegisterGrant: 'Sign-up grant',
    // WEB-2506: грант кредитами при первой оплате тарифа.
    lotFirstPaymentGrant: 'First payment grant',
    // WEB-3290: бонус при пополнении — отдельная порция-подарок.
    lotTopUpBonus: 'Top-up bonus',
    lotTierGrant: (tier: string) => `${tier} plan grant`,
    lotGrant: 'Grant',
    lotTopUp: (amount: string) => `Top-up ${amount}`,
    lotRefund: 'Refund',
    lotAdjustment: 'Adjustment',
    lotNeverExpires: 'never expires',
    lotExpiresOn: (date: string) => `expires ${date}`,
    walletLotsOrder: 'The grant is spent first, then the purchased credits.',
    // WEB-2826: свободный остаток и резерв идущих действий ИИ (сервер —
    // WEB-2837). Слово «frozen»/«заморожено» занято заморозкой организации за
    // спор — здесь только «on hold»/«в резерве».
    walletHeldLine: (amount: string) => `On hold ${amount} — AI actions in progress`,
    walletAvailableShort: (amount: string) => `Available ${amount}`,
    walletHeldInProgress: (amount: string) => `On hold ${amount} (AI actions in progress)`,
    availableBelowEstimate: (available: string, cost: string) =>
      `Available ${available} Cr — below the ${cost} Cr estimate`,

    // ── Полоса просрочки и долга ────────────────────────────────────────────
    pastDueBannerText:
      'Your subscription is past due. Update the payment method to keep using the service.',
    pastDuePagesUntil: (deadline: string) =>
      `Published pages keep working until ${deadline}. `,
    // WEB-2845 п.3 (макет WEB-2823, решения владельца 08.10 и 09.10): что
    // делать с долгом. Выход — по продаже пакетов (TOPUP_ENABLED, WEB-248):
    // включена — пополнение, выключена — поддержка (без кнопки: канала
    // поддержки в кабинете нет), участник без прав на деньги — владелец.
    // Фразы «со следующим начислением» нет: по ADR-0024 грантов нет.
    // Сумма приходит готовой строкой «340 Cr» (components/billing/debtExit.ts).
    debtTitle: (credits: string) => `Credit debt — ${credits}`,
    // WEB-3288 (решение владельца 15.10, С-28): долг закрывает только ИИ и
    // публикацию, раздачу уже опубликованных воронок не гасит.
    debtWhy:
      'Until the debt is settled, publishing and AI launches are closed. Your published funnels keep working.',
    debtExitTopUp: (credits: string) =>
      `Top up ${credits} or more — the debt is settled right away.`,
    debtExitTopUpNoAmount: 'Top up the balance — the debt is settled right away.',
    debtExitSupport:
      'Contact support — we will review the operation and lift the restriction.',
    debtExitOwner: 'Only the organization owner can settle the debt.',
    // WEB-3327: «Пополнить» при выключенной продаже пакетов (дверь
    // hooks/useOrgTopUp.ts) — тот же выход, что у долга: поддержка, без кнопки.
    topUpSupportNote: "Top-ups aren't available yet — contact support.",
    walletDebtLabel: (credits: string) => `Debt ${credits}`,
    walletDebtTopUp: 'your next top-up settles it first',
    walletDebtSupport: 'is cleared through support',
    walletDebtOwner: 'the organization owner can settle it',
    topUp: 'Top up',

    // ── Замороженный проект ─────────────────────────────────────────────────
    /** Имя объекта в кавычках языка: английские “…” против русских «…». */
    quotedName: (name: string) => `“${name}”`,
    frozenProjectsSubject: (names: string) => `Projects ${names} are suspended`,
    frozenProjectSubject: (names: string) => `Project ${names} is suspended`,
    frozenReason:
      'because of a disputed payment: the buyer disputed the charge with their bank. The project sections and its public pages are closed until the case is resolved.',
    frozenGoToBalance: 'Go to the “Balance” section',
    frozenMemberNote:
      'Ask the organization owner or the project owner — they are the ones who can sort the payment out.',

    // ── Пополнение баланса ──────────────────────────────────────────────────
    topUpTitle: 'Top up the balance',
    topUpOwnerOnly: 'The organization owner manages credit purchases',
    topUpHiddenReason:
      'The billing part is hidden by access settings: you can see the wallet balance, while credit purchases and amounts are visible to whoever manages the money of the organization.',
    topUpUpsellTitle: 'A plan upgrade is required',
    topUpUpsellDefault: 'Top-ups are not available on the Free plan. Switch to PRO.',
    topUpForbiddenTitle: 'Not enough rights',
    topUpForbiddenDefault:
      'Topping up the balance is available to the organization owner or an administrator.',
    topUpUnavailableTitle: 'Top-ups are coming soon',
    topUpDisabledText: 'Credit top-ups are not available yet — coming soon.',
    topUpUnavailableDefault: 'Credit top-ups are not available yet. Coming soon.',
    topUpUpsellError: 'Top-ups are not available on the Free plan.',
    topUpForbiddenError: 'Not enough rights to top up the balance.',
    topUpUnavailableError: 'Top-ups are not available yet.',
    topUpStartFailed: 'Could not start the top-up payment.',

    // WEB-3761 юнит 1 — «Баланс/Тариф» на ките (эталон design/cabinet-rescin/
    // sections/org.jsx, тексты TB): карточка баланса, окно пополнения,
    // «Дополнительно». Первой фразы эталона «Credits never expire» в подсказке
    // нет: у порций кода бывает срок («expires …»).
    balanceLabel: 'Balance',
    balanceHelp: 'Spent on AI and domains. 1 credit = 1 cent',
    creditsCount: (formatted: string, count: number) =>
      `${formatted} ${plural('en', count, { one: 'credit', other: 'credits' })}`,
    topUpOff: 'Top-ups open after the plan is paid',
    topUpPay: (amount: string) => `Pay ${amount}`,
    balanceExtra: 'More',
    walletLotsTitle: 'Balance breakdown',

    // ── Использование ───────────────────────────────────────────────────────
    usageTitle: 'Usage',
    usageOverage: 'Overage',
    usageOf: (used: string, limit: string) => `${used} of ${limit}`,
    quizStartsMonth: 'Quiz starts (month)',
    overageAlertTitle: 'The included volume is exceeded',
    overageAlertText: (units: number, amount: string) =>
      `Overage: ${units}×1000 starts, estimated extra charge ${amount}`,
    overageModalTitle: 'The start limit is exceeded',
    overageModalOk: 'Got it',
    overageModalText:
      'The included volume of quiz starts is exceeded. An overage charge or a higher plan may be needed. Details are in the billing section.',
    gaugeFunnels: 'Quizzes and paywalls',
    gaugeProjects: 'Projects',

    // ── История транзакций ──────────────────────────────────────────────────
    txTypeDeposit: 'Top-up',
    txTypeWithdrawal: 'Charge',
    txTypeAdjustment: 'Adjustment',
    txTypeRefund: 'Refund',
    txColumnDate: 'Date',
    txColumnType: 'Type',
    txColumnDescription: 'Description',
    txColumnAmount: 'Amount',
    txCardTitle: 'Transactions',
    txTypePlaceholder: 'Type',
    txEmptyTitle: 'No transactions yet',
    txLoadFailed: 'Could not load the transactions',
    // WEB-3761 юнит «история» — «История операций» на ките (эталон
    // design/cabinet-rescin/sections/org.jsx, TB.en: per, pers, types.all,
    // reset, pager): фишка «Период» вместо выбора дат, «Сбросить», листалка.
    txTypeAll: 'All',
    txPeriod: 'Period',
    txPeriod7: '7 days',
    txPeriod30: '30 days',
    txPeriod90: '90 days',
    txReset: 'Reset',
    txPager: (from: number, to: number, total: number) =>
      `${from}–${to} of ${total}`,

    // ── Описания операций в истории баланса (WEB-615) ───────────────────────
    // Строку пишет сервер по-английски и кладёт в базу; кабинет переводит её
    // НА ОТОБРАЖЕНИИ. Английская половина повторяет серверную формулировку —
    // на английском кабинете человек видит ровно то, что записано в истории.
    // Технические идентификаторы (модель, домен, номер счёта) не переводятся.
    txRefundGenerationFailed: (inner: string, tail: string) =>
      `Refund (generation failed): ${inner} — ${tail}`,
    txRefundWithReason: (reason: string, inner: string) =>
      `Refund (${reason}): ${inner}`,
    txRefundOf: (inner: string) => `Refund: ${inner}`,
    txRefund: 'Refund',
    // WEB-2866: закрытие недостачи сорванной операции (перерисовка, догон).
    txRefundRegenerationNotDelivered: 'Refund (image regeneration not delivered)',
    txRefundOperationFailedRetried: 'Refund (operation failed, refund retried)',
    // WEB-2865: часть суммы строки ушла на погашение долга (взыскание WEB-2459).
    // WEB-2970: поступление — не только пополнение (начисление поддержкой,
    // подписка), подпись нейтральна, как русская «Из поступления».
    txDebtCollectedNote: (amount: string) =>
      `${amount} of this incoming credit paid off the debt`,
    txShortfallClosedNote: (amount: string) =>
      `${amount} of this refund paid off the debt`,
    txAiFunnelPartial: 'AI funnel generation (partial usage)',
    txAiFunnel: (params: string) => `AI funnel generation (${params})`,
    txAiImages: (count: string) => `AI image generation (${count} image(s))`,
    txAppContext: (name: string) => `App context: ${name}`,
    // Возвращаемый тип назван явно: без него компилятор выводит у английской
    // половины ЛИТЕРАЛЬНЫЙ тип из двух строк, и русская половина под него уже
    // не подходит — правило «RU выводится из EN» ломалось бы на ровном месте.
    txAiTranslation: (partial: boolean): string =>
      partial ? 'AI translation — partial' : 'AI translation',
    txAiTranslationCatchUp: (lines: string, partial: boolean) =>
      `AI translation catch-up (${lines} lines${partial ? ' — partial' : ''})`,
    txDomainPurchaseRace: (domain: string) =>
      `Domain purchase (parallel attempt): ${domain}`,
    txDomainPurchase: (domain: string) => `Domain purchase: ${domain}`,
    txDomainPurchaseNotCompleted: (domain: string) =>
      `Domain purchase not completed: ${domain}`,
    txDomainRenewal: (domain: string) => `Domain renewal: ${domain}`,
    txDomainRenewalPayment: (domain: string) =>
      `Domain renewal payment: ${domain}`,
    txDomainRenewalRefundedToCard: (domain: string) =>
      `Domain renewal refunded to card: ${domain}`,
    txBulkRenewalFailure: (details: string) => `Bulk renewal failure: ${details}`,
    txBulkRenewalError: (details: string) => `Bulk renewal error: ${details}`,
    txTopUpCredits: (invoice: string) => `Top-up credits (invoice ${invoice})`,
    txSubscriptionGrant: (plan: string, invoice: string) =>
      `Subscription grant — ${plan} (invoice ${invoice})`,
    // WEB-4104: период — словом кабинета, а не серверным month/year; без периода (null) — без запятой.
    txFirstPaymentGrant: (plan: string, period: 'month' | 'year' | null, invoice: string) =>
      `First payment grant — ${plan}${
        period === 'month' ? ', monthly' : period === 'year' ? ', annual' : ''
      } (invoice ${invoice})`,
    txFreeOnboardingGrant: (details: string) => `Free onboarding grant (${details})`,
    txTrialGrant: (details: string) => `Trial grant (${details})`,
    txAdminCredit: (details: string) => `Admin credit (${details})`,
    txGrantLotExpired: (details: string) => `Grant lot expired: ${details}`,

    checkoutFailed: 'Could not start the payment. Please try again.',
    noProjectsCheckoutUnavailable:
      'The organization has no projects yet — payment is unavailable.',

    // ── Тарифы: единицы измерения ───────────────────────────────────────────

    // ── Нет проекта для денежного действия ──────────────────────────────────
    noBillingProjectHint:
      'The active organization has no available project. Payments and top-ups are addressed by a project of the organization — create a project to manage the plan and the balance.',
    notEnoughCredits: 'Not enough credits — top up the balance, the operation did not start',

    // ── Подключение Stripe: режим ───────────────────────────────────────────
    stripeModeLive: 'Live',
    stripeModeSandbox: 'Sandbox',
    stripeModeOff: 'Off',
    stripeKeysLive: 'Live keys',
    stripeKeysTest: 'Test keys',
    stripeKeysTestWithHint: 'Test keys (sandbox)',
    stripeModeLiveAdjective: 'live',
    stripeModeTestAdjective: 'test',
    /** Женский род нужен русской половине («боевая среда»); в английском форма одна. */
    stripeModeLiveAdjectiveFem: 'live',
    stripeModeTestAdjectiveFem: 'test',
    /** Соединитель списка наборов ключей: «Боевые ключи и Тестовые ключи». */
    keySetJoin: ' and ',
    paymentEnvAria: 'Payment environment',

    // ── Каталог Stripe ──────────────────────────────────────────────────────
    intervalOneTime: 'one-time',
    intervalDay: 'per day',
    intervalWeek: 'per week',
    intervalMonth: 'per month',
    intervalYear: 'per year',
    priceStateActive: 'Active',
    priceStateActiveHint:
      'The price is live in the connected account and is offered in the editor.',
    priceStateArchived: 'Archived',
    priceStateArchivedHint:
      'The price is archived in Stripe itself — new payments through it are impossible.',
    priceStateMissing: 'Not in Stripe',
    priceStateMissingHint:
      'The price no longer exists in the account: it was deleted in the Stripe dashboard. It is hidden from price selection.',
    priceStateForeign: 'Previous account',
    priceStateForeignHint:
      'The price is left over from previous keys (a different account or mode). It cannot be charged with the current keys, so it is hidden from price selection.',
    catalogLoadFailed: 'Could not load the products and prices',
    catalogSyncedPrices: (available: number) => `Synced with Stripe: ${available} prices available`,
    catalogSyncedHidden: (hidden: number) =>
      `${hidden} hidden (not in Stripe or from previous keys)`,
    catalogSyncedAdded: (products: number, prices: number) =>
      `added: ${products} products, ${prices} prices`,
    catalogSyncedMissing: (missing: number) => `no longer in the account: ${missing}`,
    catalogSyncFailed: 'Could not sync with Stripe',
    priceRestored: 'The price is back on sale',
    priceArchived: 'The price is archived',
    priceChangeFailed: 'Could not change the price',
    archivePriceTitle: 'Archive the price?',
    archivePriceText:
      'The price will no longer be offered in the editor, and new payments through it will become impossible. Subscriptions already taken out keep renewing. The action is reversible.',
    // Ревизия пачки 16 (R3-4): адрес второй двери. «Обратимо» без адреса —
    // обещание, которое человеку негде исполнить: в «Магазин → Товары»
    // возврата в продажу нет намеренно (граница WEB-1902).
    archiveRestoreWhere:
      'You can put it back on sale in Project settings → Payments → Products and prices.',
    archive: 'Archive',
    restore: 'Restore',
    catalogColumnProduct: 'Product',
    catalogColumnPrice: 'Price',
    catalogColumnMode: 'Mode',
    catalogColumnStatus: 'Status',
    catalogTagLive: 'Live',
    catalogTagTest: 'Test',
    catalogTitle: 'Products and prices',
    catalogLiveHint: 'Live keys are connected — only live prices are offered in the editor',
    catalogTestHint: 'Test keys are connected — only test prices are offered in the editor',
    catalogHiddenSuffix: (hidden: number) => `. Hidden from price selection: ${hidden}`,
    catalogSync: 'Sync with Stripe',
    catalogEmptyTitle: 'No prices',
    catalogEmptyDescription: 'Create a price in Stripe and press “Sync with Stripe”',

    // ── Форма подключения Stripe ────────────────────────────────────────────
    skipForeignProject: 'Belongs to another project',
    skipOwnerUnknown: 'The owner could not be verified — Stripe returned no purchase data',
    keysMixedEnvToast: 'The keys come from different environments — check the pair before connecting',
    keysLiveInTestRow: 'These are live keys — connect them in the live keys row',
    keysTestInLiveRow: 'These are test keys — connect them in the test keys row',
    stripeConnected: 'Stripe is connected',
    connectFailed: 'Connection error',
    publishableKeyLabel: 'Publishable key',
    publishableKeyIsSecret:
      'This is a secret key — it goes in the field below. Here we need the publishable one, it starts with pk_',
    publishableKeyHint: 'The publishable key starts with pk_live_ or pk_test_',
    publishableKeyPlaceholder: 'pk_live_... or pk_test_...',
    secretKeyPlaceholder: 'sk_live_... or sk_test_...',
    secretKeyLabel: 'Secret key',
    secretKeyIsPublishable:
      'This is a publishable key — it goes in the field above. Here we need the secret one, it starts with sk_',
    secretKeyHint: 'The secret key starts with sk_live_ or sk_test_',
    secretKeyStored: 'Stored encrypted, never shown again',
    keysMixedEnvAlert: (publishable: string, secret: string) =>
      `The keys come from different environments: the publishable one is ${publishable}, the secret one is ${secret}. Take both keys from the same Stripe environment (Live or Test).`,
    keysLiveInTestRowAlert:
      'This is the row for TEST keys, but live keys (pk_live_/sk_live_) were entered. Take the keys from the Stripe test environment (Test mode), or connect these ones in the live keys row.',
    keysTestInLiveRowAlert:
      'This is the row for LIVE keys, but test keys (pk_test_/sk_test_) were entered. Take the keys from the Stripe live environment (Live mode), or connect these ones in the test keys row.',
    keysLiveWarning: 'The keys are live — payments will charge real money',
    keysTestWarning:
      'The keys are test ones — payments go to the Stripe sandbox, with no real charges',
    webhookSecretLabel: 'Webhook secret (optional)',
    connect: 'Connect',
    reconnect: 'Reconnect',
    skippedImportTitle: 'Not all subscriptions were transferred',
    connectedReadWarning: 'Stripe is connected — please read the warning',
    understoodContinue: 'Understood, continue',
    skippedImportAlertTitle: 'Some subscriptions were not moved to this project',
    skippedImportAlertText:
      'The same Stripe account can be connected to several projects. Subscribers marked as belonging to another project do not move together with the keys — otherwise their data and paid access would end up in someone else’s cabinet. If a transfer is needed, move such subscriptions with a separate decision.',
    skippedImportCounts: (imported: number, total: number, skipped: number) =>
      `Subscriptions transferred: ${imported} of ${total}. Skipped: ${skipped}.`,
    skippedColumnSubscription: 'Subscription',
    skippedColumnReason: 'Why it was skipped',
    connectYourStripe: 'Connect your Stripe account',
    keysLiveIn: 'The keys live in',

    // ── Карточка подключения Stripe ─────────────────────────────────────────
    disconnectBothKeySets: (keySets: string) =>
      `Disconnecting removes BOTH key sets at once: ${keySets}. You cannot detach just one set.`,
    disconnectOneKeySet: (keySet: string) =>
      `Disconnecting removes the key set “${keySet}”.`,
    disconnectAccessWarning:
      'Together with the keys, access is revoked for every buyer of the project. To take payments again the keys will have to be connected anew.',
    // WEB-750 А4 (решение владельца 07.09, В2) — отвязка ОДНОГО набора ключей.
    disconnectKeySetButton: (keySet: string) => `Detach ${keySet}`,
    disconnectKeySetsLive: 'live keys',
    disconnectKeySetsTest: 'test keys',
    disconnectSingleKeySet: (keySet: string) =>
      `Disconnecting removes ONLY the key set “${keySet}”. The other set and its webhook stay untouched.`,
    disconnectSingleKeySetAccessWarning:
      'Access is revoked only for buyers whose subscriptions belong to this environment; buyers of the other environment keep theirs.',
    // WEB-750 В3 — строка рядом с переключателем среды.
    sandboxLiveSubscriptionsHint:
      'In the sandbox, live buyer subscriptions keep working and being charged.',
    noActiveSubscriptions: 'There are no active buyer subscriptions.',
    activeSubscriptionsCount: (count: number) => `Active buyer subscriptions: ${count}.`,
    stillBillingCount: (count: number) => ` Of them, still charging money: ${count}.`,
    // WEB-1934 — subscriptions whose Stripe environment (live/test) is
    // unknown: the resolver rejects the owner's choice on them, disconnecting
    // is refused until they are sorted out manually.
    unknownModeSubscriptionCount: (count: number) =>
      ` Of them, with an unknown environment: ${count} — the "cancel" choice will not apply to them, disconnecting will be rejected. Sort them out manually.`,
    subscriptionChoiceIntro:
      'Subscriber access and the subscriptions in Stripe are different things. Choose what to do with the subscriptions: while we hold the keys we can still change them; after disconnecting we cannot.',
    cancelNowTitle: 'Cancel the subscriptions now',
    cancelNowText:
      'There will be no more charges. Access for all subscribers stops immediately — they will not get the rest of what they paid for.',
    cancelAtPeriodEndTitle: 'Cancel at the end of the paid period',
    cancelAtPeriodEndText:
      'There will be no more charges. Access lives until the end of the period already paid for and expires on its own — the buyer gets everything they paid for.',
    keepSubscriptionsTitle: 'Leave them, I will sort it out in Stripe',
    keepSubscriptionsText:
      'We do not touch the subscriptions. Access for all subscribers stops immediately, while charges continue until you cancel the subscriptions in the Stripe dashboard. After disconnecting we can no longer do it for you.',
    keepSubscriptionsWarning: (count: number) =>
      `${count} ${count === 1 ? 'buyer' : 'buyers'} will keep paying for access that is already switched off.`,
    keepSubscriptionsWarningText:
      'Access for those buyers stops immediately, while the charges continue until you cancel the subscriptions in Stripe. The decision and the number of subscriptions left running will be written to the project action log (“Audit → Project team”) — that is the only thing that will show the choice was a deliberate one.',
    disconnectKeysExplain:
      'We will delete the keys and remove the event endpoints of every connection of the project — both live and test. We do not delete prices and products: we mark them as missing in Stripe, the amounts on subscriptions already sold are kept, but these prices cannot be selected on paywalls.',
    disconnectPaywallsExplain:
      'The paywalls stay, but they cannot be paid through. Reconnect the same account and the prices and products come back to life and payments work again. With a different account the prices on the paywalls have to be selected anew.',
    paymentsOff: 'Payments are switched off',
    paymentsOnSandbox: 'Payments are switched on — the sandbox (test keys) is active right now',
    paymentsOnLive: 'Payments are switched on — live mode is active right now',
    paymentsToggleFailed: 'Could not change payment acceptance',
    switchedToLive: 'The payment environment is switched to live',
    switchedToTest: 'The payment environment is switched to test',
    switchEnvFailed: 'Could not switch the payment environment',
    switchToLiveTitle: 'Switch to the live environment?',
    switchToLiveText:
      'Buyers will start being charged real money — test payments (the sandbox) will no longer be accepted.',
    switchAction: 'Switch',
    connectionStatusFailed: 'Could not load the connection status',
    // WEB-2006: first status request failed and we've never had a confirmed
    // status — a dedicated "unknown" state, not the honest "not connected"
    // and not the connect form (the owner is not disconnected, we just could
    // not check).
    stripeStatusUnknownTitle: 'Could not check the connection',
    stripeStatusUnknownText:
      "The server did not respond — this doesn't mean payments are off.",
    subscriptionCountFailed:
      'Could not find out how many subscriptions the disconnect would affect. Please try again.',
    disconnectProviderTitle: 'Disconnect the provider?',
    detach: 'Detach',
    stripeDetached: 'Stripe is detached',
    detachFailed: 'Error while detaching',
    paymentsConnectedOwnerOnly: 'Payments are connected. Details and management are available to the owner.',
    paymentsNotConnected: 'Payments are not connected.',
    stripeNotConnectedAskOwner: 'Stripe is not connected. Contact the project owner to set it up.',
    paymentsOffKeysKept: 'Payments are switched off — the keys and settings are kept',
    paymentEnvLabel: 'Payment environment:',
    envLive: 'Live',
    envTest: 'Test',
    envAuto: (env: string) => `Auto (${env})`,
    hideKeyManagement: 'Hide key management',
    keyManagement: 'Key management',
    keysConnectedSince: (key: string, date: string) => `${key} · since ${date}`,
    keysNotConnected: 'not connected',
    reconnectNote:
      'Re-connect opens an empty form — the old secrets are not filled in. “Cancel” keeps the current keys.',
    stripeConnectedTitle: 'Stripe is connected',
    tagSandbox: 'Sandbox',
    tagPaymentsOff: 'Acceptance off',
    tagLiveMode: 'Live mode',
    paymentsOnShort: 'Payments are switched on',
    paymentsOffShort: 'Payments are switched off',
    publishableKeyPlain: 'Publishable key',
    connectedAt: 'Connected:',

    // ── События вебхуков Stripe ─────────────────────────────────────────────
    webhookGroupPayment: 'Payments',
    webhookGroupSubscriptions: 'Subscriptions',
    webhookGroupRenewals: 'Renewals',
    webhookGroupRefunds: 'Refunds',
    webhookGroupDisputes: 'Disputes (chargebacks)',
    webhookGroupServerCharges: 'Server-side charges',
    webhookGroupCatalog: 'Products and prices',
    webhookPaymentSucceeded: 'Payment succeeded',
    webhookAsyncPaymentSucceeded: 'Delayed payment succeeded',
    webhookAsyncPaymentFailed: 'Payment failed',
    webhookSessionExpired: 'Payment session expired',
    webhookSubscriptionCreated: 'Subscription created',
    webhookSubscriptionUpdated: 'Subscription updated',
    webhookSubscriptionDeleted: 'Subscription canceled',
    webhookInvoicePaid: 'Renewal paid',
    webhookInvoiceFailed: 'Renewal not paid',
    webhookRefundCreated: 'Refund created',
    webhookRefundUpdated: 'Refund updated',
    webhookDisputeCreated: 'Dispute opened',
    webhookDisputeClosed: 'Dispute closed',
    webhookIntentSucceeded: 'Charge succeeded',
    webhookIntentFailed: 'Charge declined',
    webhookProductCreated: 'Product created',
    webhookProductUpdated: 'Product updated',
    webhookProductDeleted: 'Product deleted',
    webhookPriceCreated: 'Price created',
    webhookPriceUpdated: 'Price updated',
    webhookPriceDeleted: 'Price deleted',
    webhookAutoNote:
      'Configured automatically on connect — nothing has to be set up in the Stripe dashboard, and the list is not editable.',
    webhookListTitle: 'Which events we subscribe to',
    webhookAutoCount: (count: number) => `Events are configured automatically (${count} types)`,

    // ── Магазин: вкладки ────────────────────────────────────────────────────
    storeTitle: 'Subscriptions',
    storeSubtitle: 'Products, subscriptions and buyers — across every payment provider of the project',
    balanceOrgSubtitle: (name: string) => `Organization: ${name}`,
    storePickProject: 'Pick a project to open “Subscriptions”.',
    storePickProjectCustomer: 'Pick a project to see the buyer.',
    tabProducts: 'Products',
    tabSubscriptions: 'Subscriptions',
    tabCustomers: 'Customers',
    // WEB-4035: поимённый список лидов — вкладка раздела «Подписки».
    tabLeads: 'Leads',
    leadsNoAccess: 'You have no access to the project leads',
    storeNoAccessTitle: 'The section is unavailable',
    storeNoAccessSubtitle:
      'Your role has no access to the “Subscriptions” section. Ask the project owner to grant the rights in the “Team” section.',

    // ── Магазин: товары ─────────────────────────────────────────────────────
    intervalOptionMonth: 'Month',
    intervalOptionYear: 'Year',
    intervalOptionOneTime: 'One-time',
    productColumnName: 'Name',
    productUsedIn: (places: string) => `Used in: ${places}`,
    productUsedInPaywalls: (count: number) =>
      `${count} ${count === 1 ? 'paywall' : 'paywalls'}`,
    productColumnPrice: 'Price',
    productColumnProvider: 'Provider',
    productColumnProviderId: 'Provider ID',
    productColumnStatus: 'Status',
    archiveStripeOnly: 'Archiving is available for Stripe only for now',
    productStatusActive: 'Active',
    productStatusArchived: 'Archived',
    // WEB-3768: подпись периода в строке цены «Товаров» (сервер собирает её по-русски).
    storePriceUnitDay: 'day',
    storePriceUnitDays: 'days',
    storePriceUnitWeek: 'wk',
    storePriceUnitMonth: 'mo',
    storePriceUnitYear: 'yr',
    storePriceOneTime: 'one-time',
    productColumnAccessDuration: 'Access period',
    accessDurationUnset: 'Not set',
    accessDurationDays: (days: number) => `${days} d`,
    accessDurationStripeOnly: 'Editing the period is available for Stripe only for now',
    accessDurationOwnerOnly:
      'Available to the project owner and to an administrator with the right to manage money',
    accessDurationEditHint: 'Change the access period for a one-time purchase',
    edit: 'Edit',
    importDone: (provider: string, created: number, prices: number) =>
      `Import from ${provider}: ${created} products, ${prices} prices`,
    importMissingSuffix: (missing: number) => `, ${missing} gone at the provider`,
    importForbidden:
      'Not enough rights for this action. Catalog import is available to the project owner.',
    importFailed: 'Could not run the import. Check the payment system connection.',
    productFormIncomplete: 'Fill in the provider, the name and the amount',
    productCreated: 'The product is created',
    productCreateForbidden:
      'Not enough rights for this action. Creating a product is available to the project owner.',
    productCreateFailed: 'Could not create the product',
    accessDurationCleared: 'The access period is cleared: the server will not grant access through this price',
    accessDurationSaved: (days: string) => `Access period: ${days} d`,
    accessDurationForbidden:
      'Not enough rights for this action. Editing the catalog is available to the project owner and to an administrator with the right to manage money.',
    accessDurationSaveFailed: 'Could not save the access period',
    searchByName: 'Search by name',
    filterStatus: 'Status',
    filterProvider: 'Provider',
    importFrom: (provider: string) => `Import from ${provider}`,
    connectProviderFirst: 'Connect a payment system first',
    createProduct: 'Create a product',
    create: 'Create',
    noProviderConnectedText: 'No payment system is connected. Connect a provider to create and import products —',
    // WEB-2438 Ж2б: вкладки «Интеграции» с платежами нет — «Платежи».
    noProviderConnectedLink: 'Project settings → Payments',
    productsEmptyNoProvider: 'No products',
    productsEmptyTitle: 'No products yet',
    productsEmptyNoProviderHint: 'Connect a payment system in the project settings',
    productsEmptyHint: 'Import prices from the provider or create one manually',
    fieldProvider: 'Provider',
    fieldName: 'Name',
    fieldNamePlaceholder: 'Yearly subscription',
    fieldAmount: 'Amount',
    fieldCurrency: 'Currency',
    fieldInterval: 'Interval',
    fieldTrialDays: 'Trial, days',
    // WEB-2298 п.5: поле работает только для списаний через наше API.
    fieldAccessDurationDays: 'Access period (days, API charges only)',
    accessDurationExplain:
      'The access period for a one-time purchase, in days — only for charges your server makes through our API; a regular checkout does not use it. Empty — no access is granted through this price.',
    trialLocalFieldNote:
      'The trial is a local checkout field: it is not transferred to the provider price.',
    accessDurationModalTitle: (name: string) => `Access period — ${name}`,

    // ── Магазин: подписки ───────────────────────────────────────────────────
    cancelSubscriptionTitle: (email: string) => `Cancel the subscription of ${email}?`,
    cancelSubscriptionText:
      'The subscription stays active until the end of the paid period, then it will not renew. The money is not refunded.',
    cancelSubscriptionOk: 'Cancel the subscription',
    subscriptionCanceled: 'The subscription will not renew after the end of the paid period',
    subscriptionCancelFailed: 'Could not cancel the subscription',
    alreadyRefunded: 'Already refunded',
    refundTitle: 'Full refund?',
    refundText:
      'The buyer’s access in the app will be revoked. A partial refund is not supported.',
    refundOk: 'Refund the money',
    refundPartialWarning:
      'The money is refunded and access is revoked, but the payment system did not confirm the subscription cancellation — check it in Stripe',
    refundDone: 'The refund is done — access is revoked',
    subColumnPlan: 'Plan',
    subColumnStatus: 'Status',
    subColumnStarted: 'Started',
    subCancelsOn: (date: string) => `Cancels on ${date}`,
    subAccessUntil: (date: string) => `Access until ${date}`,
    subTrialUntil: (date: string) => `Trial until ${date}`,
    subActionCancel: 'Cancel the subscription',
    subActionRefund: 'Refund the money',
    searchByEmail: 'Search by email',
    subsReadOnlyNote:
      'Cancelling and refunding are available to the project owner only — the table is in view mode',
    subsEmptyTitle: 'No subscriptions yet',
    subsEmptyDescription: 'They will appear after the first checkout on a paywall',
    subStatusActive: 'Active',
    subStatusPastDue: 'Past due',
    subStatusPaused: 'Paused',
    subStatusCanceled: 'Canceled',
    subStatusRefunded: 'Refunded',
    subTrialFilter: 'Trial',

    // ── Магазин: покупатели ─────────────────────────────────────────────────
    customersTitle: 'Customers',
    customerDisputeOpen: 'The payment is disputed',
    customerDisputeLost: 'The dispute is lost',
    customerColumnSubscriptions: 'Subscriptions',
    ltvSortNote: 'Sorting works within the current page only (there is no server-side LTV sort)',
    customerColumnProvider: 'Provider',
    customerColumnFirstPayment: 'First payment',
    customerColumnLastPayment: 'Last payment',
    showTestPayments: 'Show test payments',
    customersEmptyTitle: 'No customers yet',
    customersEmptyDescription: 'They will appear after the first purchase',
    customerNotFound: 'Buyer not found',
    customerNotFoundHint:
      'Check the email in the page address — there is no such buyer in “Subscriptions”.',
    customerLoadFailed: 'Could not load the buyer',
    customerFlagActive: 'Active subscription',
    customerFlagTrial: 'Trial',
    customerFirstSeen: (date: string) => `First seen ${date}`,
    customerNoActiveSubscription: 'No active subscription',
    customerOneTimeOnly:
      'The buyer made a one-time purchase only — there will be no recurring charges. Everything known about them is in the history below.',
    detailFieldStatus: 'Status',
    detailFieldProvider: 'Provider',
    detailFieldStarted: 'Started',
    detailActionsHint: 'Cancelling and refunding are on the “Subscriptions” tab',
    detailActions: 'Actions',

    // ── Магазин: журнал событий покупателя ──────────────────────────────────
    eventRestored: 'restored',
    eventFieldReason: 'Reason',
    eventFieldSubscription: 'Subscription',
    eventFieldOccurred: 'Happened',
    eventFieldReceived: 'We learned',
    timelineLoadFailed: 'Could not load the event history',
    timelineEmptyTitle: 'No events yet',
    timelineEmptyDescription: 'Payments, cancellations and refunds for this buyer will appear here',
    timelineShowMore: (left: number) => `Show more · ${left} left`,
    timelinePartialNote:
      'The subscription was taken out before the log existed — the history is restored only in part. Early events may not have been kept.',
    timelineTitle: 'Event history',
    eventTrialStarted: 'The trial started',
    eventSubscriptionStarted: 'The subscription was taken out',
    eventTrialConverted: 'The trial ended, charges started',
    eventRenewed: 'The payment went through',
    eventRenewalCanceled: 'Renewal is off — the subscription lives out the paid period',
    eventRenewalResumed: 'Renewal is on again',
    eventPaymentProblem: 'The payment failed',
    eventPaymentResolved: 'The payment problem is resolved',
    eventPaused: 'The subscription is paused',
    eventResumed: 'The subscription is resumed',
    eventExpired: 'The subscription ended',
    eventRefunded: 'A refund was issued',
    eventChargebackOpened: 'A dispute arrived from the bank',
    eventChargebackResolved: 'The dispute is resolved',
    eventPlanChanged: 'The plan was changed',
    reasonUserCanceled: 'The buyer canceled it themselves',
    reasonPaymentRetriesExhausted: 'Charge attempts are exhausted',
    reasonChargeback: 'A dispute from the bank',
    reasonPlanChanged: 'A plan change',
    reasonRefunded: 'A refund was issued',
    reasonProviderDisconnected: 'The Stripe connection was detached',
    reasonGracePeriodExpired: 'The grace period after a failed charge has expired',
    reasonRevokedAccessStillBilling: 'A charge for access that is already revoked',

    // ── Возвраты: отказы ────────────────────────────────────────────────────
    refundAlready: 'This subscription has already been refunded — the money does not leave twice',
    refundNoPayment: 'There were no payments on this subscription — there is nothing to refund',
    refundCheckFailed:
      'Could not check whether this subscription was already refunded — try again later; if it is urgent, check the payment in the Stripe dashboard',
    refundUnknown: 'Could not issue the refund',

    // ── Фильтры магазина ────────────────────────────────────────────────────
    filterAll: 'All',

    // ── Магазин: подписки, постраничность (WEB-2072) ────────────────────────
    subsShownOfTotal: (range: string, total: number) =>
      `Showing ${range} of ${total}`,
    subsPageScopeNote:
      'Filters, search and sorting apply to the open page; the total counts every subscription of the project. With a filter on, a page can be shorter than usual or even empty — that does not mean nothing was found.',
  },
  {
    balanceLoadFailed: 'Не удалось загрузить баланс',
    balanceLoadFailedHint: 'Проверьте соединение и попробуйте ещё раз',
    // WEB-2806: лимит трат организации на ИИ в сутки и в месяц.
    aiSpendLimitTitle: 'Лимит трат на ИИ',
    // WEB-3761 юнит «лимит трат»: тексты по эталону (org.jsx, TB.ru limit…limNo).
    aiSpendLimitHelp: 'Запуск ИИ сверх лимита не начнётся, кредиты не спишутся',
    // WEB-3808: лимит вводится целыми долларами, траты — суммой `$12.34`.
    aiSpendLimitDaily: 'В день, $',
    aiSpendLimitMonthly: 'В месяц, $',
    aiSpendLimitNone: 'Без лимита',
    aiSpendLimitToday: 'Сегодня',
    aiSpendLimitMonth: 'За месяц',
    aiSpendLimitHeld: 'Придержано',
    aiSpendLimitHeldTip:
      'Придержано идущими действиями ИИ — спишется, когда они закончатся',
    aiSpendLimitTimezone: (tz: string) =>
      `Сутки и месяц — по часовому поясу владельца организации: ${tz}`,
    aiSpendLimitSave: 'Сохранить',
    aiSpendLimitSaved: 'Лимит сохранён',
    aiSpendLimitInvalid: 'Целое число от 1',
    aiSpendLimitNoAccess: 'Менять лимит может тот, кто распоряжается деньгами',
    aiSpendLimitSaveFailed: 'Не удалось сохранить лимит',
    aiSpendLimitLoadFailed: 'Не удалось загрузить лимит трат на ИИ',

    planOwnerOnly: 'Тарифом и оплатой распоряжается владелец организации',
    planHiddenReason:
      'Платёжная часть скрыта настройками доступа: тариф, суммы и история операций видны тем, кто распоряжается деньгами организации.',
    perMonthShort: '/мес',
    planTrialUntil: (until: string) => `Пробный до ${until}`,
    planTrialUntilWithPrice: (until: string, price: string) =>
      `Пробный до ${until}, дальше ${price}/мес`,
    planCancelsOn: (date: string) => `Подписка будет отменена ${date}`,
    planRenewsOn: (date: string) => `Продлится ${date}`,
    planGrantedManually: 'Тариф предоставлен вручную — без даты продления',
    noProjectsSettingUnavailable: 'В организации пока нет проектов — настройка недоступна.',
    settingChangeFailed: 'Не удалось изменить настройку',
    noProjectsBillingUnavailable: 'В организации пока нет проектов — биллинг недоступен.',
    portalOpenFailed: 'Не удалось открыть портал биллинга',
    pastDueTitle: 'Оплата не прошла — обновите карту',
    pastDueNoDeadline:
      'Мы попробуем списать ещё раз в течение 3 дней. Если оплата не пройдёт, тариф будет понижен до Free.',
    updateCard: 'Обновить карту',
    paidFromBalanceTitle: 'Списание с карты не прошло — тариф оплачен с баланса',
    paidFromBalanceText:
      'Мы покрыли этот расчётный период с вашего баланса кредитов. Обновите карту, чтобы следующее списание прошло как обычно.',
    manageSubscription: 'Управление подпиской',
    payFromBalanceToggle: 'Оплачивать тариф с баланса, если карта не прошла',

    // WEB-3302 юнит «тариф» (эталон org.jsx, TB.ru)
    planLabel: 'Тариф',
    planHelp: 'Один тариф: всё включено, без лимитов. Плюс плата за переходы',
    planStatusActive: 'Активен',
    planStatusTrial: 'Пробный период',
    planStatusNotPaid: 'Не оплачено',
    planStatusPaymentFailed: 'Оплата не прошла',
    planUnpaidTitle: 'Тариф не оплачен',
    planUnpaidText: 'Публикация, домены и ИИ закрыты',
    planPay: (amount: string | null) => (amount ? `Оплатить ${amount}` : 'Оплатить тариф'),
    planPastDueUntil: (deadline: string) =>
      `Страницы работают до ${deadline}, потом — «не оплачено»`,
    planNoneTitle: 'Начните пробный период',
    planNoneRule: (days: number, exits: number, exitsText: string) =>
      `${days} ${plural('ru', days, { one: 'день', few: 'дня', many: 'дней' })} или ${exitsText} ${plural('ru', exits, { one: 'переход', few: 'перехода', many: 'переходов' })} — что наступит раньше`,
    planNoneStart: 'Начать пробный период',
    // WEB-4008 (эталон TB.ru; подписи до нажатия — ответ сессии 52)
    // WEB-4180: noneHelp эталона c9ab75a2 (суммы — planTerms, без гранта)
    planNoneHelp: (rule: string, monthly: string, annualMonthly: string) =>
      `${rule}. Нужна карта. Дальше — ${monthly} в месяц или ${annualMonthly} в месяц при оплате за год. Отменить можно в любой момент. Пробный даётся один раз на человека. До пробного — один проект-черновик без публикации. На пробном домен не купить — свой подключить можно. При первой оплате — кредиты на ИИ в подарок`,
    planPeriodLabel: 'Период оплаты',
    planPeriodMonth: (amount: string) => `Помесячно ${amount}`,
    planPeriodYear: (amountPerMonth: string) => `За год ${amountPerMonth}/мес`,
    // WEB-4180: earlyLine эталона c9ab75a2
    planTrialEarlyEnd: (_exits: number, exitsText: string) =>
      `На ${exitsText}-м переходе пробный закончится раньше срока — и списание тоже`,
    planOwnerFixes: 'Исправит владелец',
    planCheckoutForbidden: 'Оформить тариф может владелец или администратор с доступом к деньгам',
    // WEB-4180: карточки периода, окно TrialAsk, плашка оплаты (эталон TB.ru c9ab75a2)
    planPickMonthLabel: 'Помесячно',
    planPickMonthNote: 'Списывается каждый месяц',
    planPickYearLabel: 'За год',
    planPickPerMonth: (amount: string) => `${amount}/мес`,
    planPickYearNote: (charge: string, save: string | null) =>
      save ? `${charge} сразу, экономия ${save}` : `${charge} сразу`,
    planPayNow: (amount: string) => `Оплатить ${amount}`,
    planChargeNow: (amount: string) => `спишем ${amount}`,
    planPayMonth: (amount: string) => `Оплатить ${amount} за месяц`,
    planPayYear: (amount: string) => `Оплатить ${amount} за год`,
    planGetTitle: 'Оформить тариф',
    planUsedByYou: 'Вы уже использовали пробный период — тариф оформится сразу',
    planUsedByOrg: 'В этой организации пробный период уже использован — тариф оформится сразу',
    planUsedHelpYou:
      'Пробный период даётся один раз на человека — вы уже брали его в другой организации. Нужна карта, отменить можно в любой момент. При первой оплате — кредиты на ИИ в подарок',
    planUsedHelpOrg:
      'Нужна карта, отменить можно в любой момент. При первой оплате — кредиты на ИИ в подарок',
    planSaveLine: (annualMonthly: string, save: string) =>
      `${annualMonthly}/мес при оплате за год — экономия ${save}`,
    planEndedTitle: 'Пробный период закончился',
    planEndedText: 'Оплатите тариф — всё заработает',
    trialAskWhyPublish: 'Публикация откроется с пробным периодом',
    trialAskWhyProject: 'Второй проект — с пробным периодом',
    trialAskWhyLimit: 'Это откроется с пробным периодом',
    trialAskWhyAi: 'Режим Pro и разбор ссылки — с пробным периодом',
    trialAskToPlan: 'К тарифу',
    // WEB-3302 юнит «условия»: окно «Тарифы и условия» (эталон TB.ru.pr, org.jsx:643–653)
    prTitle: 'Тарифы и условия',
    prHelp:
      'Подписка одна на организацию. Плата за объём — по каждому проекту: свои платежи — за переходы, платежи через нас — процент с поступлений',
    prColOwn: 'Свои платежи',
    prColOwnSub: 'оплату принимаете у себя',
    prColUs: 'Платежи через нас',
    prColUsSub: 'покупатель платит через наш Stripe',
    prColEnt: 'Enterprise',
    prColEntSub: 'большой объём, свои условия',
    prDeal: 'по договору',
    prGrpFunnels: 'Воронки и конструктор',
    prGrpAnalytics: 'Аналитика и реклама',
    prGrpApp: 'Приложение, домены, команда',
    prGrpPay: 'Оплата и объём',
    prGrpStart: 'Старт и списания',
    prRowFunnels: 'Квизы, пейволлы и воронки',
    prRowAi: 'ИИ: генерация и правки воронок',
    prRowAnalytics: 'Аналитика и A/B-тесты',
    prRowEvents: 'События в Meta и TikTok с сервера',
    prRowApp: 'Подключение приложения: SDK, ключи, события на ваш сервер',
    prRowDomains: 'Свои домены с сертификатом',
    prRowMembers: 'Участники и роли',
    prRowBuyer: 'Оплата покупателя',
    prRowVolume: 'Плата за объём',
    prRowTiers: 'Ступени',
    prRowStart: 'Как начать',
    prRowCharges: 'Списания',
    prUnlimited: 'без лимитов',
    prForCredits: 'за кредиты',
    prNoExtra: 'без доплаты',
    prOnYourSide: 'у вас',
    prViaStripe: 'через наш Stripe',
    prPerRedirect: 'за переход на пейволл',
    prBoth: 'больший из двух: процент с поступлений или переходы',
    prBothTip:
      'Для каждого проекта считаются оба способа и берётся больший — чтобы небольшой платёж не обнулял плату за переходы. Процент включится, когда заработает единый учёт поступлений, пока — переходы',
    prStartEnt: 'После разговора с нами',
    prInvoiceEnt: 'Счётом по договору',
    prIncTrial: 'На пробном периоде — всё, кроме кредитов на ИИ и покупки домена',
    prTrial: (days: number, exits: number, exitsText: string) =>
      `Пробный: ${days} ${plural('ru', days, { one: 'день', few: 'дня', many: 'дней' })} или ${exitsText} ${plural('ru', exits, { one: 'переход', few: 'перехода', many: 'переходов' })}, нужна карта`,
    prThreshold: (amount: string) =>
      `${amount} накопилось — сразу с карты, остаток — в конце месяца`,
    prYearSave: (annual: string, savings: string) =>
      `за год — ${annual}, на ${savings} дешевле`,
    prYearOnly: (annual: string) => `за год — ${annual}`,
    tierFirst: (count: string) => `Первые ${count}`,
    tierFree: 'бесплатно',
    tierUpTo: (count: string) => `До ${count}`,
    tierRange: (from: string, to: string) => `${from}–${to}`,
    tierAbove: (count: string) => `Свыше ${count}`,
    tierAny: 'За каждый переход',
    pctUpTo: (amount: string) => `До ${amount}`,
    pctAbove: (amount: string) => `Свыше ${amount}`,
    pctAny: 'С любых поступлений',
    prOff: 'Условия тарифа сейчас недоступны — обновите страницу позже',
    // WEB-3302 юнит «условия»: плашка долга по объёму (эталон TB.ru, org.jsx:617)
    debtT: (amount: string | null) =>
      amount ? `Долг за переходы — ${amount}` : 'Долг за переходы',
    debtD: 'Списание тарифа приостановлено до оплаты долга',
    debtBtn: 'Оплатить долг',
    usageDebtNotPayable:
      'Долг не удалось погасить: счёт недоступен для оплаты. Напишите в поддержку.',
    usageDebtNothingDue: 'Долга нет — платить нечего.',
    usageDebtPayFailed: 'Не удалось оплатить долг — попробуйте ещё раз',
    trialDaysLabel: 'Дни',
    trialExitsLabel: 'Переходы',
    trialExitsLeft: (count: number, formatted: string) =>
      `Осталось ${formatted} ${plural('ru', count, { one: 'переход', few: 'перехода', many: 'переходов' })}`,
    trialOf: (used: string, total: string) => `${used} из ${total}`,
    trialThenPaid: 'потом начнётся оплаченный месяц',
    payFromBalanceHint: 'Только купленными кредитами и только целиком — подарок на тариф не идёт',

    trialDaysLeftTag: (days: number) => `Осталось ${days} дн.`,

    lotTrialGrant: 'Пробный грант',
    lotOnboardingGrant: 'Приветственный грант',
    lotAdminRegisterGrant: 'Грант при регистрации',
    lotFirstPaymentGrant: 'Грант первой оплаты',
    // WEB-3290: бонус при пополнении — отдельная порция-подарок.
    lotTopUpBonus: 'Бонус к пополнению',
    lotTierGrant: (tier: string) => `Грант тарифа ${tier}`,
    lotGrant: 'Грант',
    lotTopUp: (amount: string) => `Пополнение ${amount}`,
    lotRefund: 'Возврат средств',
    lotAdjustment: 'Корректировка',
    lotNeverExpires: 'бессрочно',
    lotExpiresOn: (date: string) => `сгорает ${date}`,
    walletLotsOrder: 'Сначала тратится грант, потом докупленные.',
    walletHeldLine: (amount: string) => `В резерве ${amount} — идут действия ИИ`,
    walletAvailableShort: (amount: string) => `Доступно ${amount}`,
    walletHeldInProgress: (amount: string) => `В резерве ${amount} (идут действия ИИ)`,
    availableBelowEstimate: (available: string, cost: string) =>
      `Доступно ${available} Cr — меньше оценки ${cost} Cr`,

    pastDueBannerText:
      'Ваша подписка просрочена. Обновите способ оплаты, чтобы продолжить пользоваться сервисом.',
    pastDuePagesUntil: (deadline: string) =>
      `Опубликованные страницы работают до ${deadline}. `,
    debtTitle: (credits: string) => `Долг по кредитам — ${credits}`,
    debtWhy:
      'Пока долг не погашен, публикация и запуск ИИ закрыты. Опубликованные воронки продолжают работать.',
    debtExitTopUp: (credits: string) =>
      `Пополните баланс на ${credits} или больше — долг погасится сразу.`,
    debtExitTopUpNoAmount: 'Пополните баланс — долг погасится сразу.',
    debtExitSupport:
      'Напишите в поддержку — разберём операцию и снимем ограничение.',
    debtExitOwner: 'Погасить долг может владелец организации.',
    topUpSupportNote:
      'Пополнение баланса пока недоступно — напишите в поддержку.',
    walletDebtLabel: (credits: string) => `Долг ${credits}`,
    walletDebtTopUp: 'следующее пополнение сначала погасит его',
    walletDebtSupport: 'снимается через поддержку',
    walletDebtOwner: 'погасить может владелец организации',
    topUp: 'Пополнить',

    quotedName: (name: string) => `«${name}»`,
    frozenProjectsSubject: (names: string) => `Проекты ${names} приостановлены`,
    frozenProjectSubject: (names: string) => `Проект ${names} приостановлен`,
    frozenReason:
      'из-за спорного платежа: покупатель оспорил оплату в банке. Разделы проекта и его публичные страницы закрыты, пока вопрос не решён.',
    frozenGoToBalance: 'Перейти в раздел «Баланс»',
    frozenMemberNote:
      'Обратитесь к владельцу организации или владельцу проекта — разобраться с оплатой могут они.',

    topUpTitle: 'Пополнить баланс',
    topUpOwnerOnly: 'Покупкой кредитов распоряжается владелец организации',
    topUpHiddenReason:
      'Платёжная часть скрыта настройками доступа: остаток кошелька вам виден, а покупка кредитов и суммы — тем, кто распоряжается деньгами организации.',
    topUpUpsellTitle: 'Требуется повышение тарифа',
    topUpUpsellDefault: 'Пополнение недоступно на тарифе Free. Перейдите на PRO.',
    topUpForbiddenTitle: 'Недостаточно прав',
    topUpForbiddenDefault:
      'Пополнение баланса доступно владельцу или администратору организации.',
    topUpUnavailableTitle: 'Пополнение скоро появится',
    topUpDisabledText: 'Пополнение кредитов пока недоступно — скоро появится.',
    topUpUnavailableDefault: 'Пополнение кредитов пока недоступно. Скоро появится.',
    topUpUpsellError: 'Пополнение недоступно на тарифе Free.',
    topUpForbiddenError: 'Недостаточно прав для пополнения баланса.',
    topUpUnavailableError: 'Пополнение пока недоступно.',
    topUpStartFailed: 'Не удалось запустить оплату пополнения.',

    // WEB-3761 юнит 1
    balanceLabel: 'Баланс',
    balanceHelp: 'Тратятся на ИИ и домены. 1 кредит = 1 цент',
    creditsCount: (formatted: string, count: number) =>
      `${formatted} ${plural('ru', count, { one: 'кредит', few: 'кредита', many: 'кредитов', other: 'кредита' })}`,
    topUpOff: 'Пополнение откроется после оплаты тарифа',
    topUpPay: (amount: string) => `Оплатить ${amount}`,
    balanceExtra: 'Дополнительно',
    walletLotsTitle: 'Из чего баланс',

    usageTitle: 'Использование',
    usageOverage: 'Перерасход',
    usageOf: (used: string, limit: string) => `${used} из ${limit}`,
    quizStartsMonth: 'Запуски квиза (месяц)',
    overageAlertTitle: 'Превышен включённый объём',
    overageAlertText: (units: number, amount: string) =>
      `Перерасход: ${units}×1000 запусков, ориентировочная доплата ${amount}`,
    overageModalTitle: 'Превышен лимит запусков',
    overageModalOk: 'Понятно',
    overageModalText:
      'Превышен включённый объём запусков квизов. Возможна доплата за перерасход или переход на тариф выше. Подробности — в разделе биллинга.',
    gaugeFunnels: 'Квизы и пейволлы',
    gaugeProjects: 'Проекты',

    txTypeDeposit: 'Пополнение',
    txTypeWithdrawal: 'Списание',
    txTypeAdjustment: 'Корректировка',
    txTypeRefund: 'Возврат',
    txColumnDate: 'Дата',
    txColumnType: 'Тип',
    txColumnDescription: 'Описание',
    txColumnAmount: 'Сумма',
    txCardTitle: 'История операций',
    txTypePlaceholder: 'Тип',
    txEmptyTitle: 'Операций пока нет',
    txLoadFailed: 'Не удалось загрузить транзакции',
    // WEB-3761 юнит «история»
    txTypeAll: 'Все',
    txPeriod: 'Период',
    txPeriod7: '7 дней',
    txPeriod30: '30 дней',
    txPeriod90: '90 дней',
    txReset: 'Сбросить',
    txPager: (from: number, to: number, total: number) =>
      `${from}–${to} из ${total}`,

    txRefundGenerationFailed: (inner: string, tail: string) =>
      `Возврат (генерация не удалась): ${inner} — ${tail}`,
    txRefundWithReason: (reason: string, inner: string) =>
      `Возврат (${reason}): ${inner}`,
    txRefundOf: (inner: string) => `Возврат: ${inner}`,
    txRefund: 'Возврат',
    txRefundRegenerationNotDelivered:
      'Возврат (перерисовка картинки не доставлена)',
    txRefundOperationFailedRetried:
      'Возврат (операция не удалась, повторная попытка возврата)',
    txDebtCollectedNote: (amount: string) =>
      `Из поступления погашен долг: ${amount}`,
    txShortfallClosedNote: (amount: string) =>
      `Из возврата погашен долг: ${amount}`,
    txAiFunnelPartial: 'Генерация AI-воронки (частичное списание)',
    txAiFunnel: (params: string) =>
      `Генерация AI-воронки (${params.replace('model=', 'модель ')})`,
    txAiImages: (count: string) => `Генерация изображений (${count} шт.)`,
    txAppContext: (name: string) => `Контекст приложения: ${name}`,
    txAiTranslation: (partial: boolean) =>
      partial ? 'Перевод (AI, переведено частично)' : 'Перевод (AI)',
    txAiTranslationCatchUp: (lines: string, partial: boolean) =>
      `Догон перевода (AI, строк: ${lines}${partial ? ', переведено частично' : ''})`,
    txDomainPurchaseRace: (domain: string) =>
      `Покупка домена (параллельная попытка): ${domain}`,
    txDomainPurchase: (domain: string) => `Покупка домена: ${domain}`,
    txDomainPurchaseNotCompleted: (domain: string) =>
      `Покупка домена не состоялась: ${domain}`,
    txDomainRenewal: (domain: string) => `Продление домена: ${domain}`,
    txDomainRenewalPayment: (domain: string) =>
      `Оплата продления домена: ${domain}`,
    txDomainRenewalRefundedToCard: (domain: string) =>
      `Оплата продления домена возвращена на карту: ${domain}`,
    txBulkRenewalFailure: (details: string) => `Ошибка массового продления: ${details}`,
    txBulkRenewalError: (details: string) => `Сбой массового продления: ${details}`,
    txTopUpCredits: (invoice: string) => `Пополнение кредитов (счёт ${invoice})`,
    txSubscriptionGrant: (plan: string, invoice: string) =>
      `Начисление по подписке — ${plan} (счёт ${invoice})`,
    txFirstPaymentGrant: (plan: string, period: 'month' | 'year' | null, invoice: string) =>
      `Грант первой оплаты — ${plan}${
        period === 'month' ? ', месяц' : period === 'year' ? ', год' : ''
      } (счёт ${invoice})`,
    txFreeOnboardingGrant: (details: string) => `Приветственное начисление (${details})`,
    txTrialGrant: (details: string) => `Пробное начисление (${details})`,
    txAdminCredit: (details: string) => `Начисление администратором (${details})`,
    txGrantLotExpired: (details: string) => `Истёк срок начисления: ${details}`,

    checkoutFailed: 'Не удалось запустить оплату. Попробуйте ещё раз.',
    noProjectsCheckoutUnavailable: 'В организации пока нет проектов — оплата недоступна.',


    noBillingProjectHint:
      'В активной организации нет доступного проекта. Оплата и пополнение адресуются проектом организации — создайте проект, чтобы управлять тарифом и балансом.',
    notEnoughCredits: 'Недостаточно кредитов — пополните баланс, операция не запускалась',

    stripeModeLive: 'Боевой',
    stripeModeSandbox: 'Песочница',
    stripeModeOff: 'Выключен',
    stripeKeysLive: 'Боевые ключи',
    stripeKeysTest: 'Тестовые ключи',
    stripeKeysTestWithHint: 'Тестовые ключи (sandbox)',
    stripeModeLiveAdjective: 'боевой',
    stripeModeTestAdjective: 'тестовый',
    stripeModeLiveAdjectiveFem: 'боевая',
    stripeModeTestAdjectiveFem: 'тестовая',
    keySetJoin: ' и ',
    paymentEnvAria: 'Среда оплаты',

    intervalOneTime: 'разовый',
    intervalDay: 'в день',
    intervalWeek: 'в неделю',
    intervalMonth: 'в месяц',
    intervalYear: 'в год',
    priceStateActive: 'Активна',
    priceStateActiveHint: 'Цена живая в подключённом аккаунте и предлагается в редакторе.',
    priceStateArchived: 'Архив',
    priceStateArchivedHint:
      'Цена архивирована в самом Stripe — новые оплаты по ней невозможны.',
    priceStateMissing: 'Нет в Stripe',
    priceStateMissingHint:
      'Цены больше нет в аккаунте: её удалили в дашборде Stripe. Из выбора цены скрыта.',
    priceStateForeign: 'Прежний аккаунт',
    priceStateForeignHint:
      'Цена осталась от прежних ключей (другой аккаунт или режим). Оплатить её текущими ключами нельзя, поэтому в выборе цены она скрыта.',
    catalogLoadFailed: 'Не удалось загрузить продукты и цены',
    catalogSyncedPrices: (available: number) => `Сверено со Stripe: доступно цен ${available}`,
    catalogSyncedHidden: (hidden: number) => `скрыто ${hidden} (нет в Stripe или от прежних ключей)`,
    catalogSyncedAdded: (products: number, prices: number) =>
      `добавлено: продуктов ${products}, цен ${prices}`,
    catalogSyncedMissing: (missing: number) => `больше нет в аккаунте: ${missing}`,
    catalogSyncFailed: 'Не удалось синхронизировать с Stripe',
    priceRestored: 'Цена возвращена в продажу',
    priceArchived: 'Цена архивирована',
    priceChangeFailed: 'Не удалось изменить цену',
    archivePriceTitle: 'Архивировать цену?',
    archivePriceText:
      'Цена перестанет предлагаться в редакторе, а новые оплаты по ней станут невозможны. Уже оформленные подписки продолжат продлеваться. Действие обратимо.',
    archiveRestoreWhere:
      'Вернуть в продажу можно в «Настройки проекта → Платежи → Продукты и цены».',
    archive: 'Архивировать',
    restore: 'Вернуть',
    catalogColumnProduct: 'Продукт',
    catalogColumnPrice: 'Цена',
    catalogColumnMode: 'Режим',
    catalogColumnStatus: 'Статус',
    catalogTagLive: 'Боевой',
    catalogTagTest: 'Тестовый',
    catalogTitle: 'Продукты и цены',
    catalogLiveHint: 'Подключены боевые ключи — в редакторе предлагаются только боевые цены',
    catalogTestHint: 'Подключены тестовые ключи — в редакторе предлагаются только тестовые цены',
    catalogHiddenSuffix: (hidden: number) => `. Скрыто из выбора цены: ${hidden}`,
    catalogSync: 'Синхронизировать со Stripe',
    catalogEmptyTitle: 'Нет цен',
    catalogEmptyDescription: 'Создайте цену в Stripe и нажмите «Синхронизировать со Stripe»',

    skipForeignProject: 'Принадлежит другому проекту',
    skipOwnerUnknown: 'Принадлежность проверить не удалось — Stripe не отдал данные о покупке',
    keysMixedEnvToast: 'Ключи из разных сред — сверьте пару перед подключением',
    keysLiveInTestRow: 'Это боевые ключи — подключайте их в строке боевых ключей',
    keysTestInLiveRow: 'Это тестовые ключи — подключайте их в строке тестовых ключей',
    stripeConnected: 'Stripe подключён',
    connectFailed: 'Ошибка подключения',
    publishableKeyLabel: 'Публикуемый ключ (publishable key)',
    publishableKeyIsSecret:
      'Это секретный ключ — он идёт в поле ниже. Сюда нужен публикуемый, он начинается с pk_',
    publishableKeyHint: 'Публикуемый ключ начинается с pk_live_ или pk_test_',
    publishableKeyPlaceholder: 'pk_live_... или pk_test_...',
    secretKeyPlaceholder: 'sk_live_... или sk_test_...',
    secretKeyLabel: 'Секретный ключ (secret key)',
    secretKeyIsPublishable:
      'Это публикуемый ключ — он идёт в поле выше. Сюда нужен секретный, он начинается с sk_',
    secretKeyHint: 'Секретный ключ начинается с sk_live_ или sk_test_',
    secretKeyStored: 'Хранится в зашифрованном виде, никогда не показывается повторно',
    keysMixedEnvAlert: (publishable: string, secret: string) =>
      `Ключи из разных сред: публикуемый ${publishable}, секретный ${secret}. Возьмите оба ключа из одной и той же среды Stripe (Live или Test).`,
    keysLiveInTestRowAlert:
      'Это строка ТЕСТОВЫХ ключей, а введены боевые (pk_live_/sk_live_). Возьмите ключи из тестовой среды Stripe (Test mode) или подключайте эти — в строке боевых ключей.',
    keysTestInLiveRowAlert:
      'Это строка БОЕВЫХ ключей, а введены тестовые (pk_test_/sk_test_). Возьмите ключи из боевой среды Stripe (Live mode) или подключайте эти — в строке тестовых ключей.',
    keysLiveWarning: 'Ключи боевые — оплаты будут списывать реальные деньги',
    keysTestWarning:
      'Ключи тестовые — оплаты идут в песочнице Stripe, без реальных списаний',
    webhookSecretLabel: 'Секрет вебхука (webhook secret, опционально)',
    connect: 'Подключить',
    reconnect: 'Переподключить',
    skippedImportTitle: 'Перенеслись не все подписки',
    connectedReadWarning: 'Stripe подключён — прочитайте предупреждение',
    understoodContinue: 'Понятно, продолжить',
    skippedImportAlertTitle: 'Часть подписок не перенесена в этот проект',
    skippedImportAlertText:
      'Один и тот же аккаунт Stripe можно подключить к нескольким проектам. Подписчики, помеченные чужим проектом, вместе с ключами не переезжают — иначе их данные и оплаченный доступ оказались бы в чужом кабинете. Если перенос нужен, переносите такие подписки отдельным решением.',
    skippedImportCounts: (imported: number, total: number, skipped: number) =>
      `Перенесено подписок: ${imported} из ${total}. Пропущено: ${skipped}.`,
    skippedColumnSubscription: 'Подписка',
    skippedColumnReason: 'Почему пропущена',
    connectYourStripe: 'Подключите ваш Stripe-аккаунт',
    keysLiveIn: 'Ключи лежат в',

    disconnectBothKeySets: (keySets: string) =>
      `Отключение убирает ОБА набора ключей сразу: ${keySets}. Отвязать только один набор нельзя.`,
    disconnectOneKeySet: (keySet: string) => `Отключение убирает набор ключей «${keySet}».`,
    disconnectAccessWarning:
      'Вместе с ключами отзывается доступ у всех покупателей проекта. Чтобы вернуть приём оплаты, ключи придётся подключить заново.',
    // WEB-750 А4 (решение владельца 07.09, В2) — отвязка ОДНОГО набора ключей.
    disconnectKeySetButton: (keySet: string) => `Отвязать ${keySet}`,
    disconnectKeySetsLive: 'боевые ключи',
    disconnectKeySetsTest: 'тестовые ключи',
    disconnectSingleKeySet: (keySet: string) =>
      `Отключение убирает ТОЛЬКО набор ключей «${keySet}». Второй набор и его вебхук не тронуты.`,
    disconnectSingleKeySetAccessWarning:
      'Доступ отзывается только у покупателей с подписками этой среды; у покупателей второй среды он сохраняется.',
    // WEB-750 В3 — строка рядом с переключателем среды.
    sandboxLiveSubscriptionsHint:
      'В песочнице боевые подписки покупателей продолжают работать и списываться.',
    noActiveSubscriptions: 'Действующих подписок покупателей нет.',
    activeSubscriptionsCount: (count: number) => `Действующих подписок покупателей: ${count}.`,
    stillBillingCount: (count: number) => ` Из них продолжат списывать деньги: ${count}.`,
    // WEB-1934 — подписки с неизвестной средой Stripe (livemode не записан):
    // выбор владельца («отменить сейчас»/«в конце периода») на них НЕ
    // исполнится — resolver отказывает, отвязка будет отклонена, пока их не
    // разберут вручную (backend/src/stripe/dto/stripe.dto.ts).
    unknownModeSubscriptionCount: (count: number) =>
      ` Из них с неизвестной средой: ${count} — выбор «отменить» на них не исполнится, отвязка будет отклонена. Разберите вручную.`,
    subscriptionChoiceIntro:
      'Доступ у подписчиков и сами подписки в Stripe — разные вещи. Выберите, что сделать с подписками: пока ключи у нас, мы ещё можем их изменить; после отключения — уже нет.',
    cancelNowTitle: 'Отменить подписки сейчас',
    cancelNowText:
      'Списаний больше не будет. Доступ у всех подписчиков прекращается сразу — оплаченный остаток они не дополучат.',
    cancelAtPeriodEndTitle: 'Отменить в конце оплаченного периода',
    cancelAtPeriodEndText:
      'Списаний больше не будет. Доступ живёт до конца уже оплаченного периода и гаснет сам — покупатель дополучает то, за что заплатил.',
    keepSubscriptionsTitle: 'Оставлю, разберусь в Stripe сам',
    keepSubscriptionsText:
      'Подписки не трогаем. Доступ у всех подписчиков прекращается сразу, а списания продолжатся, пока вы не отмените подписки в панели Stripe. После отключения сделать это за вас мы уже не сможем.',
    keepSubscriptionsWarning: (count: number) =>
      `${count} ${plural('ru', count, { one: 'покупатель', few: 'покупателя', many: 'покупателей' })} ${count === 1 ? 'продолжит' : 'продолжат'} платить за доступ, который уже отключён.`,
    keepSubscriptionsWarningText:
      'Доступ у этих покупателей прекратится сразу, а списания продолжатся, пока вы не отмените подписки в Stripe. Решение и число оставленных подписок будут записаны в журнал действий проекта (раздел «Аудит» → «Команда проекта») — это единственное, что покажет, что выбор был осознанным.',
    disconnectKeysExplain:
      'Удалим ключи и снимем точки приёма событий у всех подключений проекта — и боевого, и тестового. Цены и товары не удаляем: пометим их как отсутствующие в Stripe, суммы по уже проданным подпискам сохранятся, но выбрать эти цены на пейволлах будет нельзя.',
    disconnectPaywallsExplain:
      'Пейволлы останутся, но оплатить по ним будет нельзя. Вернёте тот же аккаунт — цены и товары оживут и оплата заработает. С другим аккаунтом цены на пейволлах придётся выбрать заново.',
    paymentsOff: 'Приём оплаты выключен',
    paymentsOnSandbox: 'Приём оплаты включён — сейчас активна песочница (тестовые ключи)',
    paymentsOnLive: 'Приём оплаты включён — сейчас активен боевой режим',
    paymentsToggleFailed: 'Не удалось изменить приём оплаты',
    switchedToLive: 'Среда оплаты переключена на боевую',
    switchedToTest: 'Среда оплаты переключена на тестовую',
    switchEnvFailed: 'Не удалось переключить среду оплаты',
    switchToLiveTitle: 'Переключить в боевую среду?',
    switchToLiveText:
      'С покупателей начнут списываться настоящие деньги — тестовые платежи (песочница) больше приниматься не будут.',
    switchAction: 'Переключить',
    connectionStatusFailed: 'Не удалось загрузить статус подключения',
    stripeStatusUnknownTitle: 'Не смогли проверить подключение',
    stripeStatusUnknownText:
      'Сервер не ответил — это не значит, что оплата отключена.',
    subscriptionCountFailed:
      'Не удалось узнать, сколько подписок затронет отключение. Повторите попытку.',
    disconnectProviderTitle: 'Отключить провайдера?',
    detach: 'Отвязать',
    stripeDetached: 'Stripe отвязан',
    detachFailed: 'Ошибка при отвязке',
    paymentsConnectedOwnerOnly: 'Оплата подключена. Подробности и управление доступны владельцу.',
    paymentsNotConnected: 'Оплата не подключена.',
    stripeNotConnectedAskOwner:
      'Stripe не подключён. Обратитесь к владельцу проекта для настройки.',
    paymentsOffKeysKept: 'Приём платежей выключен — ключи и настройки сохранены',
    paymentEnvLabel: 'Среда оплаты:',
    envLive: 'Боевая',
    envTest: 'Тестовая',
    envAuto: (env: string) => `Авто (${env})`,
    hideKeyManagement: 'Скрыть управление ключами',
    keyManagement: 'Управление ключами',
    keysConnectedSince: (key: string, date: string) => `${key} · с ${date}`,
    keysNotConnected: 'не подключены',
    reconnectNote:
      'Re-connect открывает пустую форму — старые секреты не подставляются. «Отмена» сохраняет текущие ключи.',
    stripeConnectedTitle: 'Stripe подключён',
    tagSandbox: 'Песочница',
    tagPaymentsOff: 'Приём выключен',
    tagLiveMode: 'Боевой режим',
    paymentsOnShort: 'Приём оплаты включён',
    paymentsOffShort: 'Приём оплаты выключен',
    publishableKeyPlain: 'Публикуемый ключ (publishable key)',
    connectedAt: 'Подключено:',

    webhookGroupPayment: 'Оплата',
    webhookGroupSubscriptions: 'Подписки',
    webhookGroupRenewals: 'Продления',
    webhookGroupRefunds: 'Возвраты',
    webhookGroupDisputes: 'Споры (чарджбеки)',
    webhookGroupServerCharges: 'Серверные списания',
    webhookGroupCatalog: 'Продукты и цены',
    webhookPaymentSucceeded: 'Оплата прошла',
    webhookAsyncPaymentSucceeded: 'Отложенная оплата прошла',
    webhookAsyncPaymentFailed: 'Оплата не прошла',
    webhookSessionExpired: 'Сессия оплаты истекла',
    webhookSubscriptionCreated: 'Подписка создана',
    webhookSubscriptionUpdated: 'Подписка изменена',
    webhookSubscriptionDeleted: 'Подписка отменена',
    webhookInvoicePaid: 'Продление оплачено',
    webhookInvoiceFailed: 'Продление не оплачено',
    webhookRefundCreated: 'Возврат создан',
    webhookRefundUpdated: 'Возврат обновлён',
    webhookDisputeCreated: 'Спор открыт',
    webhookDisputeClosed: 'Спор закрыт',
    webhookIntentSucceeded: 'Списание прошло',
    webhookIntentFailed: 'Списание отклонено',
    webhookProductCreated: 'Продукт создан',
    webhookProductUpdated: 'Продукт изменён',
    webhookProductDeleted: 'Продукт удалён',
    webhookPriceCreated: 'Цена создана',
    webhookPriceUpdated: 'Цена изменена',
    webhookPriceDeleted: 'Цена удалена',
    webhookAutoNote:
      'Настроены автоматически при подключении — настраивать в кабинете Stripe ничего не нужно, список не редактируется.',
    webhookListTitle: 'На какие события мы подписаны',
    webhookAutoCount: (count: number) => `События настроены автоматически (${count} типов)`,

    storeTitle: 'Подписки',
    storeSubtitle: 'Товары, подписки и покупатели — по всем платёжным провайдерам проекта',
    balanceOrgSubtitle: (name: string) => `Организация: ${name}`,
    storePickProject: 'Выберите проект, чтобы открыть «Подписки».',
    storePickProjectCustomer: 'Выберите проект для просмотра покупателя.',
    tabProducts: 'Товары',
    tabSubscriptions: 'Подписки',
    tabCustomers: 'Клиенты',
    // WEB-4035: поимённый список лидов — вкладка раздела «Подписки».
    tabLeads: 'Лиды',
    leadsNoAccess: 'Нет доступа к лидам проекта',
    storeNoAccessTitle: 'Раздел недоступен',
    storeNoAccessSubtitle:
      'У вашей роли нет доступа к разделу «Подписки». Попросите владельца проекта выдать права в разделе «Команда».',

    intervalOptionMonth: 'Месяц',
    intervalOptionYear: 'Год',
    intervalOptionOneTime: 'Разово',
    productColumnName: 'Название',
    productUsedIn: (places: string) => `Используется: ${places}`,
    productUsedInPaywalls: (count: number) =>
      `${count} ${plural('ru', count, { one: 'пейволл', few: 'пейволла', many: 'пейволлов' })}`,
    productColumnPrice: 'Цена',
    productColumnProvider: 'Провайдер',
    productColumnProviderId: 'ID у провайдера',
    productColumnStatus: 'Статус',
    archiveStripeOnly: 'Архивация доступна пока только для Stripe',
    productStatusActive: 'Активен',
    productStatusArchived: 'Архив',
    storePriceUnitDay: 'дн',
    storePriceUnitDays: 'дн',
    storePriceUnitWeek: 'нед',
    storePriceUnitMonth: 'мес',
    storePriceUnitYear: 'год',
    storePriceOneTime: 'разово',
    productColumnAccessDuration: 'Срок доступа',
    accessDurationUnset: 'Не задан',
    accessDurationDays: (days: number) => `${days} дн.`,
    accessDurationStripeOnly: 'Правка срока доступна пока только для Stripe',
    accessDurationOwnerOnly:
      'Доступно владельцу проекта и администратору с правом распоряжаться деньгами',
    accessDurationEditHint: 'Изменить срок доступа за разовую покупку',
    edit: 'Изменить',
    importDone: (provider: string, created: number, prices: number) =>
      `Импорт из ${provider}: товаров ${created}, цен ${prices}`,
    importMissingSuffix: (missing: number) => `, пропало у провайдера ${missing}`,
    importForbidden:
      'Недостаточно прав для этого действия. Импорт каталога доступен владельцу проекта.',
    importFailed: 'Не удалось выполнить импорт. Проверьте подключение платёжной системы.',
    productFormIncomplete: 'Заполните провайдера, название и сумму',
    productCreated: 'Товар создан',
    productCreateForbidden:
      'Недостаточно прав для этого действия. Создание товара доступно владельцу проекта.',
    productCreateFailed: 'Не удалось создать товар',
    accessDurationCleared: 'Срок доступа снят: сервер не будет выдавать доступ по этой цене',
    accessDurationSaved: (days: string) => `Срок доступа: ${days} дн.`,
    accessDurationForbidden:
      'Недостаточно прав для этого действия. Правка каталога доступна владельцу проекта и администратору с правом распоряжаться деньгами.',
    accessDurationSaveFailed: 'Не удалось сохранить срок доступа',
    searchByName: 'Поиск по названию',
    filterStatus: 'Статус',
    filterProvider: 'Провайдер',
    importFrom: (provider: string) => `Импортировать из ${provider}`,
    connectProviderFirst: 'Сначала подключите платёжную систему',
    createProduct: 'Создать товар',
    create: 'Создать',
    noProviderConnectedText:
      'Платёжная система не подключена. Подключите провайдера, чтобы создавать и импортировать товары —',
    // WEB-2438 Ж2б: вкладки «Интеграции» с платежами нет — «Платежи».
    noProviderConnectedLink: 'Настройки проекта → Платежи',
    productsEmptyNoProvider: 'Нет продуктов',
    productsEmptyTitle: 'Товаров пока нет',
    productsEmptyNoProviderHint: 'Подключите платёжную систему в настройках проекта',
    productsEmptyHint: 'Импортируйте цены от провайдера или создайте вручную',
    fieldProvider: 'Провайдер',
    fieldName: 'Название',
    fieldNamePlaceholder: 'Годовая подписка',
    fieldAmount: 'Сумма',
    fieldCurrency: 'Валюта',
    fieldInterval: 'Интервал',
    fieldTrialDays: 'Триал, дней',
    // WEB-2298 п.5: поле работает только для списаний через наше API.
    fieldAccessDurationDays: 'Срок доступа (дней, только для списаний через API)',
    accessDurationExplain:
      'Срок доступа за разовую покупку, дней — только для списаний, которые ваш сервер делает через наше API; при обычной оплате не используется. Пусто — доступ по этой цене не выдаётся.',
    trialLocalFieldNote:
      'Триал — локальное поле чекаута: на прайс провайдера он не переносится.',
    accessDurationModalTitle: (name: string) => `Срок доступа — ${name}`,

    cancelSubscriptionTitle: (email: string) => `Отменить подписку ${email}?`,
    cancelSubscriptionText:
      'Подписка останется активной до конца оплаченного периода, потом не продлится. Деньги не возвращаются.',
    cancelSubscriptionOk: 'Отменить подписку',
    subscriptionCanceled: 'Подписка не будет продлена после конца оплаченного периода',
    subscriptionCancelFailed: 'Не удалось отменить подписку',
    alreadyRefunded: 'Уже возвращено',
    refundTitle: 'Полный возврат средств?',
    refundText:
      'Доступ покупателя в приложении будет отозван. Частичный возврат не поддерживается.',
    refundOk: 'Вернуть средства',
    refundPartialWarning:
      'Деньги возвращены и доступ отозван, но платёжная система не подтвердила отмену подписки — проверьте её в Stripe',
    refundDone: 'Возврат выполнен — доступ отозван',
    subColumnPlan: 'План',
    subColumnStatus: 'Статус',
    subColumnStarted: 'Начата',
    subCancelsOn: (date: string) => `Отменится ${date}`,
    subAccessUntil: (date: string) => `Доступ до ${date}`,
    subTrialUntil: (date: string) => `Триал до ${date}`,
    subActionCancel: 'Отменить подписку',
    subActionRefund: 'Вернуть средства',
    searchByEmail: 'Поиск по email',
    subsReadOnlyNote:
      'Отмена и возврат доступны только владельцу проекта — таблица в режиме просмотра',
    subsEmptyTitle: 'Подписок пока нет',
    subsEmptyDescription: 'Появятся после первого оформления в пейволле',
    subStatusActive: 'Активна',
    subStatusPastDue: 'Просрочена',
    subStatusPaused: 'На паузе',
    subStatusCanceled: 'Отменена',
    subStatusRefunded: 'Возврат',
    subTrialFilter: 'Триал',

    customersTitle: 'Клиенты',
    customerDisputeOpen: 'Платёж оспаривается',
    customerDisputeLost: 'Спор проигран',
    customerColumnSubscriptions: 'Подписки',
    ltvSortNote: 'Сортировка — в пределах текущей страницы (серверной сортировки по LTV нет)',
    customerColumnProvider: 'Провайдер',
    customerColumnFirstPayment: 'Первый платёж',
    customerColumnLastPayment: 'Последний платёж',
    showTestPayments: 'Показывать тестовые оплаты',
    customersEmptyTitle: 'Клиентов пока нет',
    customersEmptyDescription: 'Появятся после первой покупки',
    customerNotFound: 'Покупатель не найден',
    customerNotFoundHint:
      'Проверьте почту в адресе страницы — такого покупателя в «Подписках» нет.',
    customerLoadFailed: 'Не удалось загрузить покупателя',
    customerFlagActive: 'Активная подписка',
    customerFlagTrial: 'Пробный период',
    customerFirstSeen: (date: string) => `Впервые пришёл ${date}`,
    customerNoActiveSubscription: 'Активной подписки нет',
    customerOneTimeOnly:
      'Покупатель совершил только разовую покупку — повторных списаний не будет. Всё, что по нему известно, — в истории ниже.',
    detailFieldStatus: 'Статус',
    detailFieldProvider: 'Провайдер',
    detailFieldStarted: 'Начата',
    detailActionsHint: 'Отмена и возврат — на вкладке «Подписки»',
    detailActions: 'Действия',

    eventRestored: 'восстановлено',
    eventFieldReason: 'Причина',
    eventFieldSubscription: 'Подписка',
    eventFieldOccurred: 'Произошло',
    eventFieldReceived: 'Мы узнали',
    timelineLoadFailed: 'Не удалось загрузить историю событий',
    timelineEmptyTitle: 'Событий пока нет',
    timelineEmptyDescription: 'Здесь появятся оплаты, отмены и возвраты по этому покупателю',
    timelineShowMore: (left: number) => `Показать ещё · осталось ${left}`,
    timelinePartialNote:
      'Подписка оформлена до появления журнала — история восстановлена частично. Ранние события могли не сохраниться.',
    timelineTitle: 'История событий',
    eventTrialStarted: 'Начался пробный период',
    eventSubscriptionStarted: 'Подписка оформлена',
    eventTrialConverted: 'Пробный период закончился, начались платежи',
    eventRenewed: 'Оплата прошла',
    eventRenewalCanceled: 'Продление отключено — подписка доживает оплаченный период',
    eventRenewalResumed: 'Продление включено снова',
    eventPaymentProblem: 'Оплата не прошла',
    eventPaymentResolved: 'Проблема с оплатой устранена',
    eventPaused: 'Подписка поставлена на паузу',
    eventResumed: 'Подписка возобновлена',
    eventExpired: 'Подписка завершилась',
    eventRefunded: 'Оформлен возврат',
    eventChargebackOpened: 'Пришёл спор из банка',
    eventChargebackResolved: 'Спор разрешён',
    eventPlanChanged: 'Изменён тариф',
    reasonUserCanceled: 'Отменил сам покупатель',
    reasonPaymentRetriesExhausted: 'Исчерпаны попытки списания',
    reasonChargeback: 'Спор из банка',
    reasonPlanChanged: 'Смена тарифа',
    reasonRefunded: 'Оформлен возврат',
    reasonProviderDisconnected: 'Отвязано подключение Stripe',
    reasonGracePeriodExpired: 'Истёк льготный период после неудачного списания',
    reasonRevokedAccessStillBilling: 'Списание за уже отозванный доступ',

    refundAlready: 'По этой подписке возврат уже сделан — деньги второй раз не уходят',
    refundNoPayment: 'Платежей по этой подписке не было — возвращать нечего',
    refundCheckFailed:
      'Не удалось проверить, был ли по этой подписке возврат — повторите позже; если срочно, сверьте платёж в дашборде Stripe',
    refundUnknown: 'Не удалось выполнить возврат',

    filterAll: 'Все',

    subsShownOfTotal: (range: string, total: number) =>
      `Показано ${range} из ${total}`,
    subsPageScopeNote:
      'Отбор, поиск и сортировка применяются к открытой странице, а общее число считает все подписки проекта. При включённом отборе страница может оказаться короче обычной или пустой — это не значит, что ничего не нашлось.',
  },
);

/** Английская половина — она задаёт множество ключей и их сигнатуры. */
type BillingStringsEn = typeof billingStrings.en;

/**
 * Ключи, чьё значение — ГОТОВАЯ строка, без подстановок.
 *
 * Нужен там, где код держит таблицу «код сервера → ключ надписи» (статус
 * подписки, тип операции, состояние цены). Без него `s[key]` получает тип
 * «строка ИЛИ любая из подстановок словаря», и такое значение нельзя ни
 * отрисовать, ни положить в поле типа string.
 */
export type BillingTextKey = {
  [K in keyof BillingStringsEn]: BillingStringsEn[K] extends string ? K : never;
}[keyof BillingStringsEn];
